import { useEffect, useState } from 'react';
import { api, payments as Api, type MetaResponse, type Pass, type Payment, type PaymentInput,
  type PayMethod, type PayStatus } from '@tvx/api-client';
import { Modal } from '../components/Modal';

interface Meta extends MetaResponse {
  payMethods: PayMethod[];
  payStatus: PayStatus[];
}

export function PaymentForm({ payment, members, nextInvoice, onCancel, onSaved }: {
  payment: Payment | null;
  members: { id: number; name: string; email: string }[];
  nextInvoice: string;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [f, setF] = useState<PaymentInput>(() => payment ? {
    invoice_no: payment.invoice_no, member_id: payment.member_id, item: payment.item,
    amount: payment.amount, gst: payment.gst, method: payment.method, status: payment.status,
    pass_id: payment.pass_id, course_id: payment.course_id, batch_id: payment.batch_id,
    reference: payment.reference, note: payment.note, paid_at: payment.paid_at,
  } : {
    invoice_no: nextInvoice, member_id: null, item: '', amount: 0, method: 'UPI', status: 'paid',
    pass_id: null, course_id: null, batch_id: null, reference: '', note: '', paid_at: null,
  });
  const [meta, setMeta] = useState<Meta | null>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { void api.get<Meta>('/meta').then(setMeta).catch(() => {}); }, []);

  /* Everything that can be bought, so a recorded payment can say which it was. */
  const [sale, setSale] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    void Promise.all([
      api.get<{ items: Pass[] }>('/passes').catch(() => ({ items: [] as Pass[] })),
      api.get<{ items: { id: number; title: string }[] }>('/courses').catch(() => ({ items: [] })),
      api.get<{ items: { id: number; name: string }[] }>('/batches').catch(() => ({ items: [] })),
    ]).then(([p, c, b]) => setSale([
      ...p.items.map((x) => ({ value: `pass:${x.id}`, label: `Access · ${x.name}` })),
      ...c.items.map((x) => ({ value: `course:${x.id}`, label: `Course · ${x.title}` })),
      ...b.items.map((x) => ({ value: `batch:${x.id}`, label: `Batch · ${x.name}` })),
    ]));
  }, []);

  const set = <K extends keyof PaymentInput>(k: K, v: PaymentInput[K]) =>
    setF((prev) => ({ ...prev, [k]: v }));

  async function submit() {
    if (!f.amount) { setError('An amount is required.'); return; }
    setError('');
    setStatus('Saving…');
    try {
      if (payment) await Api.update(payment.id, f);
      else await Api.create(f);
      await onSaved();
    } catch (e) {
      setStatus('');
      setError((e as Error).message);
    }
  }

  return (
    <Modal
      title={payment ? `Edit payment · ${payment.invoice_no || payment.id}` : 'Record a payment'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bg" type="button" onClick={() => void submit()}>Save payment</button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <Field label="Invoice no.">
          <input className="inp" value={f.invoice_no} onChange={(e) => set('invoice_no', e.target.value)} />
        </Field>
        <Field label="Member">
          <select
            className="inp"
            value={f.member_id ?? ''}
            onChange={(e) => set('member_id', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Not linked to an account</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name || m.email}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Paid on">
          <input className="inp" type="date" value={(f.paid_at ?? '').slice(0, 10)}
            onChange={(e) => set('paid_at', e.target.value || null)} />
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <Field label="What was bought">
          <input className="inp" value={f.item} onChange={(e) => set('item', e.target.value)}
            placeholder="Trade Calls · 1 month" />
        </Field>
        <Field label="Amount (₹)">
          <input className="inp" type="number" min={0} value={f.amount || ''}
            onChange={(e) => set('amount', Number(e.target.value))} />
        </Field>
        <Field label="GST (₹)">
          <input className="inp" type="number" min={0} value={f.gst ?? ''}
            placeholder="Left blank: 18% of the amount, inclusive"
            onChange={(e) => set('gst', e.target.value === '' ? undefined : Number(e.target.value))} />
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <Field label="Method">
          <select className="inp" value={f.method} onChange={(e) => set('method', e.target.value as PayMethod)}>
            {(meta?.payMethods ?? [f.method]).map((m) => <option key={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select className="inp" value={f.status} onChange={(e) => set('status', e.target.value as PayStatus)}>
            {(meta?.payStatus ?? [f.status]).map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Reference">
          <input className="inp" value={f.reference} onChange={(e) => set('reference', e.target.value)}
            placeholder="UTR or transaction id" />
        </Field>
      </div>

      {/* Marking a payment paid is what opens access, so this is the part of
          the form that actually changes what a member can see. */}
      <Field label="What it bought">
        <select
          className="inp"
          value={f.pass_id ? `pass:${f.pass_id}` : f.course_id ? `course:${f.course_id}` : f.batch_id ? `batch:${f.batch_id}` : ''}
          onChange={(e) => {
            const [kind, id] = e.target.value.split(':');
            setF((prev) => ({
              ...prev,
              pass_id: kind === 'pass' ? Number(id) : null,
              course_id: kind === 'course' ? Number(id) : null,
              batch_id: kind === 'batch' ? Number(id) : null,
              item: prev.item || (sale.find((x) => x.value === e.target.value)?.label ?? prev.item),
            }));
          }}
        >
          <option value="">Nothing — just a record of money</option>
          {sale.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <p className="hint" style={{ marginTop: 6 }}>
          Marking this paid opens it for the member.
        </p>
      </Field>

      <Field label="Note">
        <textarea className="inp ta" rows={2} value={f.note} onChange={(e) => set('note', e.target.value)} />
      </Field>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="fld"><label className="lbl">{label}</label>{children}</div>;
}
